import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Section from '../components/Section';
import Button from '../components/Button';
import { CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { fetchProducts, fetchProductsPage, fetchSiteSettings } from '../lib/sanity';
import { defaultProducts, defaultProductsPage } from '../lib/defaultContent';
import type { Product, ProductsPageContent } from '../lib/types';

const products = defaultProducts;

const ProductCard: React.FC<{ product: Product; index: number; whatsappNumber: string }> = ({ product, index, whatsappNumber }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      id={product.id}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
    >
      <div className="grid md:grid-cols-2">
        {/* Image */}
        <div className="h-52 md:h-auto md:max-h-72 overflow-hidden">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 flex flex-col">
          <h3 className="text-2xl font-playfair font-bold text-primary-black mb-3">
            {product.name}
          </h3>

          <p className="text-gray-600 leading-relaxed mb-5">{product.description}</p>

          {/* Always visible: Grades */}
          <div className="mb-4">
            <h4 className="text-xs font-inter font-semibold uppercase tracking-widest text-gray-500 mb-2">
              Available Grades
            </h4>
            <ul className="space-y-1">
              {(product.grades || []).map((grade) => (
                <li key={grade} className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-red flex-shrink-0" />
                  {grade}
                </li>
              ))}
            </ul>
          </div>

          {/* Expandable details */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1.5 text-sm font-inter font-medium text-primary-red hover:text-red-700 transition-colors mb-4 self-start"
          >
            {expanded ? 'Show less' : 'Show capacity, packaging & delivery'}
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="space-y-4 mb-5"
            >
              <div>
                <h4 className="text-xs font-inter font-semibold uppercase tracking-widest text-gray-500 mb-1">
                  Bulk Supply Capacity
                </h4>
                <p className="text-sm text-gray-700">{product.bulkCapacity}</p>
              </div>
              <div>
                <h4 className="text-xs font-inter font-semibold uppercase tracking-widest text-gray-500 mb-2">
                  Packaging Options
                </h4>
                <ul className="space-y-1">
                  {(product.packaging || []).map((opt) => (
                    <li key={opt} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-secondary-sage flex-shrink-0" />
                      {opt}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-xs font-inter font-semibold uppercase tracking-widest text-gray-500 mb-2">
                  Delivery Options
                </h4>
                <ul className="space-y-1">
                  {(product.delivery || []).map((opt) => (
                    <li key={opt} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-secondary-sage flex-shrink-0" />
                      {opt}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}

        </div>
      </div>

      {/* Full-width action bar */}
      <div className="border-t border-gray-100 bg-gray-50/50 px-6 py-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <span className="text-sm text-gray-500 font-inter">Ready to order?</span>
        <div className="flex flex-col sm:flex-row gap-2">
          <a
            href={`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi! I'm interested in ordering ${product.name}.`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#25D366] text-white text-sm font-inter font-semibold rounded-lg hover:bg-[#20BA5A] transition-colors"
          >
            <img src="/icons/whatsapp.svg" alt="" className="w-4 h-4 brightness-0 invert" />
            WhatsApp
          </a>
          <a
            href={`/contact?subject=${encodeURIComponent('Bulk Quote – ' + product.name)}`}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-red text-white text-sm font-inter font-semibold rounded-lg hover:bg-red-700 transition-colors"
          >
            Request Quote
          </a>
        </div>
      </div>
    </motion.div>
  );
};

const Products: React.FC = () => {
  const [productList, setProductList] = useState<Product[]>(products);
  const [pageContent, setPageContent] = useState<ProductsPageContent>(defaultProductsPage);
  const [whatsappNumber, setWhatsappNumber] = useState<string>('+256759177625');

  useEffect(() => {
    // Prioritize fetched data, fallback to defaults
    fetchProductsPage().then((data) => {
      setPageContent(data || defaultProductsPage);
    });
    fetchProducts().then((data) => {
      if (!data || data.length === 0) return;
      const normalized = data.map((p) => ({
        ...p,
        id:
          p.id ||
          p.name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, ''),
        grades: p.grades || [],
        packaging: p.packaging || [],
        delivery: p.delivery || [],
      }));
      setProductList(normalized);
    });
    fetchSiteSettings().then((data) => {
      if (data?.whatsappNumber) setWhatsappNumber(data.whatsappNumber);
    });
  }, []);

  return (
    <main className="pt-20">
      <Section padding="default">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center mb-6"
        >
          <h1 className="text-5xl md:text-6xl font-playfair font-bold text-primary-black mb-4">
            {pageContent.headerTitle} <span className="text-primary-red">{pageContent.headerHighlight}</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
            {pageContent.headerDescription}
          </p>
        </motion.div>

        {/* Quick nav */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex flex-wrap gap-2 justify-center mb-16"
        >
          {productList.map((p) => (
            <a
              key={p.id}
              href={`#${p.id}`}
              className="px-4 py-2 bg-cream-50 hover:bg-primary-red hover:text-white text-gray-700 rounded-full text-sm font-inter font-medium border border-gray-200 hover:border-primary-red transition-all duration-200"
            >
              {p.name}
            </a>
          ))}
        </motion.div>

        {/* Product list */}
        <div className="space-y-8 mb-20">
          {productList.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} whatsappNumber={whatsappNumber} />
          ))}
        </div>

        {/* Capacity callout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-primary-black rounded-2xl p-8 md:p-12 mb-20 text-white"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-playfair font-bold mb-4">
                {pageContent.capacityTitle}
              </h2>
              <p className="text-gray-300 leading-relaxed">
                {pageContent.capacityDescription}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {pageContent.capacityStats.map((stat) => (
                <div key={stat.label} className="bg-white/10 rounded-xl p-4 text-center">
                  <div className="text-primary-red font-playfair font-bold text-2xl mb-1">
                    {stat.value}
                  </div>
                  <div className="text-gray-400 text-xs font-inter uppercase tracking-wide">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-primary-red/10 to-secondary-terracotta/10 rounded-2xl p-8 md:p-12 text-center"
        >
          <h3 className="text-3xl md:text-4xl font-playfair font-bold text-primary-black mb-4">
            {pageContent.ctaTitle}
          </h3>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
            {pageContent.ctaDescription}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="primary" href={pageContent.ctaButton1Link}>
              {pageContent.ctaButton1Text}
            </Button>
            <Button variant="secondary" href={pageContent.ctaButton2Link}>
              {pageContent.ctaButton2Text}
            </Button>
          </div>
        </motion.div>
      </Section>
    </main>
  );
};

export default Products;
